import { catalog } from './generated/catalog.js';
import type {
  ActionCard,
  CardDefinition,
  GroupId,
  PropertyGroup,
  RentCard,
  Rules,
} from './schema.js';

export * from './schema.js';
export * from './tokens.js';
export type { Catalog } from './types.js';
export { catalog };

export const rules: Rules = catalog.rules;
export const groups: PropertyGroup[] = catalog.groups;
export const cardDefinitions: CardDefinition[] = catalog.cards;
/** Invented tenants offered when a player names a wildcard. Flavor only. */
export const tenantNames: string[] = catalog.tenants;

const byId = new Map(catalog.cards.map((c) => [c.id, c]));
const groupsById = new Map(catalog.groups.map((g) => [g.id, g]));

export function getCard(id: string): CardDefinition {
  const card = byId.get(id);
  if (!card) throw new Error(`Unknown card definition "${id}"`);
  return card;
}

export function getGroup(id: GroupId): PropertyGroup {
  const group = groupsById.get(id);
  if (!group) throw new Error(`Unknown property group "${id}"`);
  return group;
}

/** Rent owed for holding `owned` properties in `group`, before any doubling. */
export function rentFor(group: GroupId, owned: number): number {
  const { rent } = getGroup(group);
  if (owned <= 0) return 0;
  return rent[Math.min(owned, rent.length) - 1]!;
}

export function groupSize(group: GroupId): number {
  return getGroup(group).size;
}

export function isUpgradeCard(card: CardDefinition): card is ActionCard {
  return card.kind === 'action' && card.play.upgrade !== undefined;
}

export function isRentCard(card: CardDefinition): card is RentCard {
  return card.kind === 'rent';
}

/** The card ids the engine needs to reference by name. */
export const CARD_IDS = {
  cancel: 'blame-the-investment-committee',
  doubleRent: 'renegotiate-the-rent',
  grocery: 'shadow-anchor-grocery-store',
  residential: 'residential-development-on-site',
} as const;

/**
 * Every card instance in a fresh deck, in manifest order. Instance ids are
 * stable across a match so transfers, replay, and animations can name a card.
 */
export function deckManifest(): { instanceId: string; definitionId: string }[] {
  const out: { instanceId: string; definitionId: string }[] = [];
  for (const card of catalog.cards) {
    for (let copy = 0; copy < card.copies; copy += 1) {
      out.push({ instanceId: `${card.id}#${copy}`, definitionId: card.id });
    }
  }
  return out;
}
